import React from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { History, Location } from 'history'
import { Container, FullWidthContainerBody } from 'uiComponents/settingsContainer'
import { CODES_TABLE_HEADER_SLOT_ID } from 'venue/bookingCodes/tableHeaderSlot'
import UploadReservationsPage from 'venue/bookingCodes/reservationCodePools/uploadReservationsPage'
import { UploadSeasonPassesPage } from 'venue/bookingCodes/uploadSeasonPassesPage'
import BarcodePoolsPage from 'venue/bookingCodes/barcodePools'
import ReservationCodePoolsPage from 'venue/bookingCodes/reservationCodePools'
import UploadBarcodesPage from 'venue/bookingCodes/barcodePools/uploadBarcodesPage'
import ProductIdsPage from 'venue/bookingCodes/productIdsPage'
import { ArticleService } from 'admin/articleService'
import { BookingCodesService } from 'venue/bookingCodes/bookingCodesService'
import { withFeatures } from 'features'
import { LoginService } from 'http/loginService'
import CodesHeading from './heading'
import VenueSectionTabs from 'venue/sectionTabs'
import { NavigationBack } from 'uiComponents/navigation/navigationBack'
import InSectionTabsSlot from 'uiComponents/navigation/sectionTabsSlot'
import { useBookingNavigation } from './hooks'
import BaseKnowledgeLink from 'uiComponents/typography/BaseKnowledgeLink'
import { useMessages } from 'messagesContext'

interface VouchersPageProps {
    history: History
    accountSlug: string
    location: Location
    articleService: ArticleService
    bookingCodesService: BookingCodesService
    backofficeEndpoint: string
    loginService: LoginService
    hasFeature: (feature: string, accountSlug: string) => boolean
}

function VouchersPage(props: VouchersPageProps) {
    const reservationPoolsOn = props.hasFeature('RetailersReservationCodes', props.accountSlug)
    const { replaceMessages, removeAllMessages } = useMessages()
    const sections = useBookingNavigation()
    const activeSection = sections.find((section) => section.active)

    const codes = `/account/${props.accountSlug}/venue/codes`
    const backLink = props.location.pathname.includes('/codes/tickets/upload/')
        ? { path: `${codes}/tickets/list/`, text: 'Back to barcode pools list' }
        : reservationPoolsOn && props.location.pathname.includes('/codes/reservations/upload/')
        ? { path: `${codes}/reservations/list/`, text: 'Back to reservation campaigns list' }
        : null

    return (
        <div style={{ position: 'relative' }} id="codes">
            <CodesHeading activeSection={activeSection} />
            <div style={{ maxWidth: '75rem' }}>
                {backLink ? (
                    <InSectionTabsSlot>
                        <div className="detail-page-back">
                            <NavigationBack onClick={() => props.history.push(backLink.path)} text={backLink.text} />
                        </div>
                    </InSectionTabsSlot>
                ) : (
                    <VenueSectionTabs />
                )}
            </div>
            <div className="codes-table-header" id={CODES_TABLE_HEADER_SLOT_ID} />
            <Container style={{ maxWidth: '75rem', marginBottom: 0 }}>
                <Switch location={props.location}>
                    <Route
                        path="/account/:accountSlug/venue/codes/tickets/list"
                        render={() => (
                            <FullWidthContainerBody>
                                <BarcodePoolsPage
                                    bookingCodesService={props.bookingCodesService}
                                    accountSlug={props.accountSlug}
                                    replaceMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                    history={props.history}
                                />
                            </FullWidthContainerBody>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/venue/codes/tickets/upload/:poolId"
                        render={() => (
                            <FullWidthContainerBody>
                                <UploadBarcodesPage
                                    history={props.history}
                                    bookingCodesService={props.bookingCodesService}
                                    loginService={props.loginService}
                                    backofficeEndpoint={props.backofficeEndpoint}
                                    accountSlug={props.accountSlug}
                                    replaceMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </FullWidthContainerBody>
                        )}
                    />
                    {!reservationPoolsOn && (
                        <Route
                            path="/account/:accountSlug/venue/codes/reservations"
                            render={() => (
                                <FullWidthContainerBody>
                                    <UploadReservationsPage
                                        history={props.history}
                                        bookingCodesService={props.bookingCodesService}
                                        loginService={props.loginService}
                                        backofficeEndpoint={props.backofficeEndpoint}
                                        accountSlug={props.accountSlug}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </FullWidthContainerBody>
                            )}
                        />
                    )}
                    {reservationPoolsOn && (
                        <Route
                            path="/account/:accountSlug/venue/codes/reservations/list/"
                            render={() => (
                                <FullWidthContainerBody>
                                    <ReservationCodePoolsPage
                                        bookingCodesService={props.bookingCodesService}
                                        loginService={props.loginService}
                                        backofficeEndpoint={props.backofficeEndpoint}
                                        accountSlug={props.accountSlug}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        history={props.history}
                                    />
                                </FullWidthContainerBody>
                            )}
                        />
                    )}
                    {reservationPoolsOn && (
                        <Route
                            path="/account/:accountSlug/venue/codes/reservations/upload/:poolId"
                            render={() => (
                                <FullWidthContainerBody>
                                    <UploadReservationsPage
                                        history={props.history}
                                        bookingCodesService={props.bookingCodesService}
                                        loginService={props.loginService}
                                        backofficeEndpoint={props.backofficeEndpoint}
                                        accountSlug={props.accountSlug}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </FullWidthContainerBody>
                            )}
                        />
                    )}
                    <Route
                        path="/account/:accountSlug/venue/codes/sold_tickets"
                        render={() => (
                            <FullWidthContainerBody>
                                <UploadSeasonPassesPage
                                    bookingCodesService={props.bookingCodesService}
                                    loginService={props.loginService}
                                    backofficeEndpoint={props.backofficeEndpoint}
                                    accountSlug={props.accountSlug}
                                    replaceMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </FullWidthContainerBody>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/venue/codes/product_ids"
                        render={() => (
                            <FullWidthContainerBody>
                                <ProductIdsPage
                                    history={props.history}
                                    accountSlug={props.accountSlug}
                                    articleService={props.articleService}
                                    replaceMessages={replaceMessages}
                                />
                            </FullWidthContainerBody>
                        )}
                    />
                    <Route render={() => <Redirect to={`/account/${props.accountSlug}/venue/codes/tickets/list`} />} />
                </Switch>
            </Container>
            {activeSection?.guidelinesUrl && <BaseKnowledgeLink link={activeSection.guidelinesUrl} />}
        </div>
    )
}

export default withFeatures(VouchersPage)
